import { Content, ContentTree } from 'src/app/models/documents/folder.model';
import { DocumentService } from 'src/app/services/document.service';
import { DriveDateSectionLoader } from './drive-date-section.loader';
import { DriveNavigationCoordinator } from './drive-navigation.coordinator';
import { DriveTreeMapper } from './drive-tree.mapper';
import { DriveItem } from './drive-viewer.models';
import { DriveWorkspaceState } from './drive-viewer.state';

export interface DriveTreeLifecyclePort {
  initVersioning(): void;
  startAutosync(): void;
  stopAutosync(): void;
}

export class DriveTreeCoordinator {
  private readonly sortKeyStorage = 'drive.sort.key';
  private readonly sortDirectionStorage = 'drive.sort.dir';

  constructor(
    private readonly state: DriveWorkspaceState,
    private readonly documents: DocumentService,
    private readonly mapper: DriveTreeMapper,
    private readonly dateSection: DriveDateSectionLoader,
    private readonly navigation: DriveNavigationCoordinator,
    private readonly lifecycle: DriveTreeLifecyclePort,
    private readonly myDriveLabel: () => string
  ) {}

  initialize(): void {
    this.loadSortPreferences();
    this.loadFolderTree();
    this.lifecycle.initVersioning();
    if (this.state.active) this.lifecycle.startAutosync();
  }

  activeChanged(active: boolean): void {
    this.state.active = active;
    if (active) {
      this.reloadAll();
      this.lifecycle.startAutosync();
    } else {
      this.lifecycle.stopAutosync();
    }
  }

  loadSortPreferences(): void {
    try {
      const key = (localStorage.getItem(this.sortKeyStorage) || '').toLowerCase();
      const direction = (localStorage.getItem(this.sortDirectionStorage) || '').toLowerCase();
      if (['name', 'type', 'owner', 'modified', 'size'].includes(key)) {
        this.state.sortKey = key as DriveWorkspaceState['sortKey'];
      }
      if (direction === 'asc' || direction === 'desc') this.state.sortDir = direction;
    } catch { /* Storage may be disabled. */ }
  }

  saveSortPreferences(): void {
    try {
      localStorage.setItem(this.sortKeyStorage, this.state.sortKey);
      localStorage.setItem(this.sortDirectionStorage, this.state.sortDir);
    } catch { /* Storage may be disabled. */ }
  }

  updateSortKey(value: DriveWorkspaceState['sortKey']): void {
    this.state.sortKey = value;
    this.saveSortPreferences();
  }

  updateSortDirection(value: DriveWorkspaceState['sortDir']): void {
    this.state.sortDir = value;
    this.saveSortPreferences();
  }

  loadFolderTree(): void {
    this.state.isLoadingFiles = true;
    this.documents.getFolderTreeStructure().subscribe({
      next: (tree) => {
        const root = this.rootNode(tree);
        this.applyInitialRoot(root);
        this.state.isLoadingFiles = false;
      },
      error: () => {
        this.applyFallbackRoot();
        this.dateSection.loadByDate();
        this.state.isLoadingFiles = false;
      }
    });
  }

  reloadAll(): void {
    this.state.items = [];
    this.state.currentFolderId = '';
    this.loadFolderTree();
  }

  refreshPreservingState(silent = false, onFinish?: () => void): void {
    if (!silent) this.state.isRefreshingTree = true;
    const currentFolderId = this.state.currentFolderId;
    const expanded = new Set(this.state.expanded);
    const selected = new Set(this.state.selectedIds);
    this.documents.getFolderTreeStructure().subscribe({
      next: (tree) => {
        const root = this.rootNode(tree);
        if (root) {
          this.applyRefreshedRoot(root, currentFolderId, expanded, selected);
          this.dateSection.loadByDate();
          this.dateSection.mergeRootDocuments();
        } else {
          this.applyFallbackRoot();
          this.dateSection.loadByDate();
          this.dateSection.mergeRootDocuments();
        }
        if (!silent) this.state.isRefreshingTree = false;
        onFinish?.();
      },
      error: () => {
        this.applyFallbackRoot();
        this.dateSection.loadByDate();
        if (!silent) this.state.isRefreshingTree = false;
        onFinish?.();
      }
    });
  }

  flattenNode(node: Content, parentId: string | null, parentPath: string[]): void {
    this.mapper.flattenNode(node, parentId, parentPath, this.state.treeBuffer);
  }

  inferKind(node: Content, children: Content[]): 'folder' | 'file' {
    return this.mapper.inferKind(node, children);
  }

  resolveDocumentId(node: Content, fallbackId: string): string | undefined {
    return this.mapper.resolveDocumentId(node, fallbackId);
  }

  loadByDateSection(): void { this.dateSection.loadByDate(); }
  mergeRootDocuments(): void { this.dateSection.mergeRootDocuments(); }

  private applyInitialRoot(root: Content | undefined): void {
    if (!root) {
      this.applyFallbackRoot();
    } else {
      this.state.items = this.mapper.flatten(root);
      this.state.currentFolderId = String(root.id);
      this.state.expanded.clear();
      this.state.expanded.add(String(root.id));
    }
    this.dateSection.loadByDate();
    this.dateSection.mergeRootDocuments();
    this.state.treeBuffer = [];
  }

  private applyRefreshedRoot(
    root: Content,
    previousCurrent: string,
    previousExpanded: Set<string>,
    previousSelected: Set<string>
  ): void {
    const items = this.mapper.flatten(root);
    this.rehydratePreviewContent(items);
    const ids = new Set(items.map((item) => item.id));
    this.state.items = items;
    this.state.currentFolderId = ids.has(previousCurrent) ? previousCurrent : String(root.id);
    this.state.expanded.clear();
    previousExpanded.forEach((id) => { if (ids.has(id)) this.state.expanded.add(id); });
    this.state.expanded.add(String(root.id));
    this.navigation.expandPathTo(this.state.currentFolderId);
    this.state.selectedIds.clear();
    previousSelected.forEach((id) => { if (ids.has(id)) this.state.selectedIds.add(id); });
    this.state.treeBuffer = [];
  }

  private rehydratePreviewContent(items: DriveItem[]): void {
    const previousById = new Map(this.state.items.map((item) => [item.id, item]));
    items.forEach((item) => {
      const previous = previousById.get(item.id);
      if (!previous) return;
      item.previewUrl ??= previous.previewUrl;
      item.previewSafeUrl ??= previous.previewSafeUrl;
      item.previewText ??= previous.previewText;
    });
  }

  private applyFallbackRoot(): void {
    const name = this.myDriveLabel();
    const root: DriveItem = {
      id: 'root', name, type: 'folder', owner: 'you', modified: new Date().toISOString(),
      size: 0, path: [name], parentId: null
    };
    this.state.items = [root];
    this.state.currentFolderId = 'root';
    this.state.expanded.clear();
    this.state.expanded.add('root');
  }

  private rootNode(tree: ContentTree): Content | undefined {
    const content = tree?.content;
    return Array.isArray(content) ? content[0] : content || undefined;
  }
}
